import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * Takes useState and makes it behave similar to how setState does in a class component.
 *
 * @example:
 *
 * const [state, setState] = useSetState({
 *  hasFlag: false,
 *  count: 0,
 * });
 *
 * setState({ hasFlag: true });
 * // OR
 * setState((prevState) => ({count: prevState.count + 1}));
 */
export const useSetState = (initialState = {}) => {
  const [state, normalSetState] = useState(initialState);

  const setState = newState => {
    if (typeof newState === 'function') {
      normalSetState(prevState => ({
        ...prevState,
        ...newState(prevState),
      }));
    } else {
      normalSetState(prevState => ({
        ...prevState,
        ...newState,
      }));
    }
  };

  return [state, setState];
};

export const FINALIZE_LOGIN_FLOW_TRANSITION = {
  IDLE: 'idle',
  SUCCESS: 'success',
  AUTH_CYCLE_STARTED: 'auth_cycle_started',
  WAITING_FOR_AUTH_CYCLE: 'waiting_for_auth_cycle',
  AWAIT_PICK_OFFICE: 'await_pick_office',
  FAILURE: 'failure',
};

/**
 * Pure transition evaluator for finalize-login flow.
 * Keeping this logic pure allows targeted unit tests for flow branches without
 * requiring a React hook test renderer.
 */
export const evaluateFinalizeLoginTransition = ({
  isFinalizing,
  isAuthenticated,
  isAuthenticating,
  isAwaitPickOffice,
  sawFinalizeAuthCycle,
}) => {
  if (!isFinalizing) {
    return FINALIZE_LOGIN_FLOW_TRANSITION.IDLE;
  }

  if (isAuthenticated) {
    return FINALIZE_LOGIN_FLOW_TRANSITION.SUCCESS;
  }

  if (isAuthenticating) {
    return FINALIZE_LOGIN_FLOW_TRANSITION.AUTH_CYCLE_STARTED;
  }

  if (!sawFinalizeAuthCycle) {
    return FINALIZE_LOGIN_FLOW_TRANSITION.WAITING_FOR_AUTH_CYCLE;
  }

  if (isAwaitPickOffice) {
    return FINALIZE_LOGIN_FLOW_TRANSITION.AWAIT_PICK_OFFICE;
  }

  return FINALIZE_LOGIN_FLOW_TRANSITION.FAILURE;
};

/**
 * Tracks the finalize-login auth cycle used by SSO interstitial screens.
 *
 * Internal state model:
 * 1) idle     -> nothing in progress
 * 2) running  -> caller has dispatched finalize and is waiting for auth state
 *
 * While running, the hook observes global auth flags and routes to one of:
 * - success callback (authenticated)
 * - await-office callback (preparer office-selection branch)
 * - failure callback (auth cycle ended without success)
 *
 * Important: this hook waits for auth processing to start at least once before
 * declaring failure. That avoids treating initial idle render as a failed
 * finalize attempt.
 *
 * @param {Object} params
 * @param {boolean} params.isAuthenticated True once app auth state is established.
 * @param {boolean} params.isAuthenticating True while finalize/auth saga is running.
 * @param {boolean} params.isAwaitPickOffice True when flow intentionally pauses for office selection.
 * @param {Function} [params.onStart] Called exactly when startFinalize() is triggered.
 * @param {Function} [params.onSuccess] Called when finalize settles with authenticated state.
 * @param {Function} [params.onAwaitPickOffice] Called when finalize settles into office-selection branch.
 * @param {Function} [params.onFailure] Called when finalize settles back to idle without success.
 * @param {Function} [params.onTransition] Optional observer invoked with transition labels.
 */
export const useFinalizeLoginFlow = ({
  isAuthenticated,
  isAuthenticating,
  isAwaitPickOffice,
  onStart,
  onSuccess,
  onAwaitPickOffice,
  onFailure,
  onTransition,
}) => {
  const [isFinalizing, setIsFinalizing] = useState(false);
  const sawFinalizeAuthCycle = useRef(false);
  const isFinalizingRef = useRef(false);
  const lastTransitionRef = useRef(FINALIZE_LOGIN_FLOW_TRANSITION.IDLE);

  // Reset local finalize-tracking state so callers can safely start another
  // finalize cycle after success/failure/await-office handling completes.
  const stopFinalize = useCallback(() => {
    isFinalizingRef.current = false;
    setIsFinalizing(false);
    sawFinalizeAuthCycle.current = false;
  }, []);

  // Entry-point used by pages after any page-specific prerequisites complete
  // (for example accepting terms, choosing account, or saving IRS preference).
  const startFinalize = useCallback(() => {
    if (isFinalizingRef.current) {
      return;
    }

    isFinalizingRef.current = true;
    sawFinalizeAuthCycle.current = false;
    setIsFinalizing(true);
    if (onStart) {
      onStart();
    }
  }, [onStart]);

  useEffect(() => {
    const transition = evaluateFinalizeLoginTransition({
      isFinalizing,
      isAuthenticated,
      isAuthenticating,
      isAwaitPickOffice,
      sawFinalizeAuthCycle: sawFinalizeAuthCycle.current,
    });

    if (
      onTransition &&
      transition !== lastTransitionRef.current &&
      transition !== FINALIZE_LOGIN_FLOW_TRANSITION.IDLE
    ) {
      onTransition(transition);
    }
    lastTransitionRef.current = transition;

    switch (transition) {
      case FINALIZE_LOGIN_FLOW_TRANSITION.IDLE:
      case FINALIZE_LOGIN_FLOW_TRANSITION.WAITING_FOR_AUTH_CYCLE:
        return;
      case FINALIZE_LOGIN_FLOW_TRANSITION.AUTH_CYCLE_STARTED:
        // Mark that auth processing actually started at least once.
        sawFinalizeAuthCycle.current = true;
        return;
      case FINALIZE_LOGIN_FLOW_TRANSITION.SUCCESS:
        // Happy path: finalize completed and app auth state is now established.
        stopFinalize();
        if (onSuccess) {
          onSuccess();
        }
        return;
      case FINALIZE_LOGIN_FLOW_TRANSITION.AWAIT_PICK_OFFICE:
        // Intentional non-authenticated branch for preparers who must pick office.
        stopFinalize();
        if (onAwaitPickOffice) {
          onAwaitPickOffice();
        }
        return;
      case FINALIZE_LOGIN_FLOW_TRANSITION.FAILURE:
      default:
        // Auth cycle ended and did not produce success or expected branch.
        stopFinalize();
        if (onFailure) {
          onFailure();
        }
    }
  }, [
    isFinalizing,
    isAuthenticated,
    isAuthenticating,
    isAwaitPickOffice,
    onTransition,
    onSuccess,
    onAwaitPickOffice,
    onFailure,
    stopFinalize,
  ]);

  return {
    isFinalizing,
    // Single lock value pages can use for both click guards and button disabled state.
    isFinalizeLocked: isFinalizing || isAuthenticating,
    startFinalize,
    stopFinalize,
  };
};
